import { useEffect, useState, useMemo } from 'react';

interface MessageObject<T> {
  channel: string;
  message: T;
}

/**
 * WebSocket hook with reconnect-with-backoff and stable channel comparison.
 * Ported from fpm-frontend so GolfTrack's power/charger pages can subscribe
 * to FPM's `power` and `charger` channels.
 */
const useWebSocket = <T,>(
  baseUrl: string,
  channels: string[],
  action: string = 'subscribe'
) => {
  const [messages, setMessages] = useState<MessageObject<T>[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const channelsSignature = JSON.stringify(channels);

  const stableChannels = useMemo(() => {
    try {
      const parsed = JSON.parse(channelsSignature);
      return Array.isArray(parsed) ? parsed.filter((c): c is string => typeof c === 'string') : [];
    } catch {
      return [];
    }
  }, [channelsSignature]);

  const wsUrl = useMemo(() => {
    const queryParams = new URLSearchParams();
    queryParams.append('action', action);
    stableChannels.forEach((channel) => queryParams.append('channel', channel));
    return `${baseUrl}?${queryParams.toString()}`;
  }, [baseUrl, stableChannels, action]);

  useEffect(() => {
    let socket: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let closedByCleanup = false;
    let reconnectAttempts = 0;

    const clearReconnectTimer = () => {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };

    const scheduleReconnect = () => {
      if (closedByCleanup) return;
      clearReconnectTimer();
      const delayMs = Math.min(1000 * Math.pow(2, reconnectAttempts), 10000);
      reconnectAttempts += 1;
      reconnectTimer = setTimeout(() => {
        connect();
      }, delayMs);
    };

    const connect = () => {
      if (closedByCleanup) return;
      socket = new WebSocket(wsUrl);

      const handleOpen = () => {
        reconnectAttempts = 0;
        setIsConnected(true);
      };

      const handleMessage = (event: MessageEvent) => {
        const parsedData: MessageObject<T> = JSON.parse(event.data);
        if (stableChannels.includes(parsedData.channel)) {
          setMessages((prevMessages) => [...prevMessages, parsedData].slice(-200));
        }
      };

      const handleClose = () => {
        setIsConnected(false);
        scheduleReconnect();
      };

      const handleError = () => {
        try {
          socket?.close();
        } catch (_) {}
      };

      socket.addEventListener('open', handleOpen);
      socket.addEventListener('message', handleMessage);
      socket.addEventListener('close', handleClose);
      socket.addEventListener('error', handleError);
    };

    connect();

    return () => {
      closedByCleanup = true;
      clearReconnectTimer();
      if (socket) {
        try {
          socket.close();
        } catch (_) {}
      }
      setIsConnected(false);
    };
  }, [wsUrl, stableChannels]);

  return { messages, isConnected };
};

export default useWebSocket;
